import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { hash, timeout, waitForProperty } from 'ember-concurrency';
import MetricsService from 'cio/services/metrics';
import { Periods, MetricDataPoint, MetricTypes, Entities, REVERSE_PERIOD_KEYS } from 'cio/utils/metrics/common';
import {
  Grouping,
  computedGroupingItems,
  GroupingItem,
  selectLargestGroupingAvailableIfNecessary,
} from 'cio/utils/metrics/resolution';
import { getDefaultSegmentMembershipRange } from 'cio/utils/metrics/segment-membership';
import { action, computed } from '@ember/object';
import SegmentController from 'cio/pods/segments/segment/controller';
import { alias } from '@ember/object/computed';
import moment from 'moment-timezone';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import merge from 'lodash.merge';
import { queryEncode } from 'cio/helpers/query';
import AuthService from 'cio/services/auth';
import { isNone } from '@ember/utils';
import config from 'cio/config/environment';
import { SegmentMembershipMetric } from 'cio/utils/metrics/models/segment-membership-metric';
import type { Options } from 'highcharts';
import { TimeRange } from 'cio/utils/time';
import SegmentModel from 'cio/models/segment';

const SEGMENT_RELOAD_INTERVAL = config.APP.segmentReloadInterval;

const DEFAULT_GROUPING = Grouping.Daily;

const COMMON_CHART_SETTINGS = {
  chart: {
    height: 330,
    spacing: [5, 5, 0, 5],
  },
  legend: {
    margin: 30,
  },
  time: {
    timezone: 'Etc/UTC',
  },
  xAxis: {
    type: 'datetime',
    startOfWeek: 1,
  },
  yAxis: {
    allowDecimals: false,
    min: null,
    labels: {
      enabled: true,
    },
  },
};

export default class SegmentPeopleController extends Controller {
  @controller('segments.segment') segmentController!: SegmentController;

  declare model: SegmentModel;

  queryParams: any = [
    {
      source: {
        replace: true,
      },
    },
  ];

  source = null;

  // For segment membership metric: maximum range needs to be 2 years to match with the backend validation
  maxYearRange = 2;

  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service('metrics') metricsService!: MetricsService;
  @service auth!: AuthService;

  @alias('segmentController.segmentMembershipChangeExportRange') timeRange!: TimeRange;
  grouping = DEFAULT_GROUPING;

  @computedGroupingItems('timeRange') groupingItems!: GroupingItem[];

  @computed('membershipMetric.total.[]')
  get isMembershipMetricAllZeroesOrEmpty() {
    let data = this.membershipMetric?.total ?? [];

    if (data.length === 0) {
      return true;
    }

    return !data.some((p) => p.y !== 0);
  }

  @computed('isMembershipMetricAllZeroesOrEmpty')
  get membershipChartOptions() {
    let chartOptions: Options = {
      xAxis: {
        minPadding: 1,
      },
      // Need to explicitly set to null, otherwise highcharts will cache
      // the previous settings (possibly min and max of 0 and 1) if data changes
      yAxis: {
        min: null,
        max: null,
      },
    };

    if (this.isMembershipMetricAllZeroesOrEmpty) {
      chartOptions.yAxis = {
        min: 0,
        max: 1,
      };
    }

    return merge({}, { ...COMMON_CHART_SETTINGS }, chartOptions);
  }

  membershipMetric: SegmentMembershipMetric | null = null;
  membershipChangeMetric: SegmentMembershipMetric | null = null;

  @computed('timeRange.{start,end}', 'resolution')
  get chartTimeRange() {
    if (!this.timeRange.start || !this.timeRange.end) {
      return null;
    }

    let range = {
      start: moment(this.timeRange.start),
      end: moment(this.timeRange.end),
    };

    if (this.resolution === Periods.WEEKS) {
      range.start = range.start.startOf('isoWeek');
      range.end = range.end.startOf('isoWeek');
    }

    if (this.resolution === Periods.MONTHS) {
      range.start = range.start.startOf('month');
      range.end = range.end.startOf('month');
    }

    return range;
  }

  @computed(
    'segmentController.isBuildingSegment',
    'pollForBuildFinish.isRunning',
    'fetchMembershipMetrics.isRunning',
    'membershipMetric',
    'membershipChangeMetric',
  )
  get isLoading() {
    return (
      this.segmentController.isBuildingSegment ||
      this.pollForBuildFinish.isRunning ||
      (this.fetchMembershipMetrics.isRunning && (isNone(this.membershipMetric) || isNone(this.membershipChangeMetric)))
    );
  }

  @computed('model.id')
  get peopleListFilters() {
    return queryEncode({
      and: [
        {
          segment: { id: this.model.id },
        },
      ],
    });
  }

  @computed('membershipMetric')
  get membershipSeries() {
    return [
      {
        name: 'Total membership',
        type: 'line',
        data: this.membershipMetric?.total ?? [],
        colorIndex: 'ink-900',
      },
    ];
  }

  @computed('membershipChangeMetric')
  get membershipChangeChartOptions() {
    let data = this.membershipChangeMetric;

    return merge({}, COMMON_CHART_SETTINGS, {
      series: [
        {
          name: 'People who entered',
          data: data?.entered ?? [],
          colorIndex: 'ink-500',
          type: 'column',
        },
        {
          name: 'People who left',
          data: (data?.left ?? []).map((p: MetricDataPoint) => ({ ...p, y: p.y == null ? null : -p.y, name: p.y })),
          colorIndex: 'ink-300',
          type: 'column',
        },
        {
          name: 'Net change',
          data: data?.netChange ?? [],
          colorIndex: 'purple-500',
          type: 'line',
        },
      ],
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
    });
  }

  @computed('grouping')
  get resolution() {
    return REVERSE_PERIOD_KEYS[this.grouping];
  }

  pollForBuildFinish = task(async (addTimeout = false) => {
    await waitForProperty(this.segmentController, 'isBuildingSegment', false);
    this.metricsService.clearMetrics(MetricTypes.SegmentMembershipMetric, Entities.SEGMENT, this.model.id);

    if (addTimeout) {
      await timeout(SEGMENT_RELOAD_INTERVAL);
    }

    this.fetchMembershipMetrics.perform();
  });

  fetchMembershipMetrics = task({ drop: true }, async () => {
    let period = REVERSE_PERIOD_KEYS[this.grouping];
    let range = { ...(this.timeRange ?? {}) };

    if (range.start && range.end) {
      range.start = moment.tz(`${range.start.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
      range.end = moment.tz(`${range.end.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
    }

    // For the "Change in membership" graph we give users the option to select a
    // "Group by", so we need to issue two separate API calls, since the regular
    // segment membership graph is permanently set to the "Daily" grouping.
    // If the change graph is set to "Daily" as well, this won't actually issue
    // two identical API calls, since the metrics service includes code to
    // prevent duplicate calls like that and instead will only issue one, while
    // returning the same result to both yields in the hash.
    let result = await hash({
      membershipMetric: this.metricsService.fetchSegmentMembershipMetrics(this.model.get('id'), Periods.DAYS, range),
      membershipChangeMetric: this.metricsService.fetchSegmentMembershipMetrics(this.model.get('id'), period, range),
    });

    this.setProperties({
      membershipMetric: result.membershipMetric,
      membershipChangeMetric: result.membershipChangeMetric,
    });
  });

  setDefaults() {
    this.setProperties({
      timeRange: getDefaultSegmentMembershipRange(),
      grouping: DEFAULT_GROUPING,
    });
  }

  @action
  onRangeSelect(timeRange: TimeRange) {
    this.set('timeRange', timeRange);
    selectLargestGroupingAvailableIfNecessary(this, 'grouping', this.groupingItems);
    this.fetchMembershipMetrics.perform();
  }

  @action
  onTrackMembershipGraphHover(time: number) {
    this.analytics.track('segment', 'segment_membership_chart_hover', {
      model: this.model,
      time,
    });
  }

  @action
  onTrackMembershipChangeGraphHover(time: number) {
    this.analytics.track('segment', 'segment_membership_change_chart_hover', {
      model: this.model,
      time,
    });
  }

  @action
  onGroupingSelect(value: Grouping) {
    this.set('grouping', value);
    this.fetchMembershipMetrics.perform();
  }
}

declare module '@ember/controller' {
  interface Registry {
    'segments.segment.people': SegmentPeopleController;
  }
}
